import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { ambApi, monthLabel } from "@/lib/ambApi";

const input = "h-11 w-full rounded-xl border border-[#D9C8B3] bg-white px-4 text-sm text-[#141013] outline-none focus:border-[#7A5418]";
const BADGE = { "à vérifier": "bg-[#F1E4CF] text-[#5E3F10]", "validée": "bg-[#DDEBDD] text-[#24502F]", "refusée": "bg-[#F5DADA] text-[#7A1F1F]" };
const MISSION = { "en cours": "Mission du mois : en cours", "validée": "Mission du mois validée par Ozentya", "non réalisée": "Mission du mois non réalisée" };

function PostForm({ onAdded }) {
  const [f, setF] = useState({ kind: "post", url: "", note: "" });
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await ambApi.post("/ambassador/posts", f); setF({ ...f, url: "", note: "" }); toast.success("Lien ajouté"); onAdded(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={add} data-testid="amb-post-form" className="mt-5 grid gap-3 md:grid-cols-[140px_1fr_1fr_auto]">
      <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="amb-post-kind" className={input}><option value="post">Post</option><option value="story">Story</option></select>
      <input required type="url" placeholder="Lien de la publication (https://…)" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="amb-post-url" className={input} />
      <input placeholder="Note (facultatif)" maxLength={300} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="amb-post-note" className={input} />
      <button disabled={busy} data-testid="amb-post-submit" className="flex h-11 items-center justify-center gap-2 rounded-full bg-[#141013] px-6 text-xs font-semibold uppercase tracking-[0.18em] text-[#FBF8F3] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Ajouter</button>
    </form>
  );
}

export function AmbPosts() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["amb-posts"], queryFn: () => ambApi.get("/ambassador/posts").then((r) => r.data) });
  const posts = Array.isArray(data?.posts) ? data.posts : [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["amb-posts"] });
  const remove = async (id) => { try { await ambApi.delete(`/ambassador/posts/${id}`); refresh(); } catch (err) { toast.error(errMsg(err)); } };
  const count = (k) => posts.filter((p) => p.kind === k).length;
  return (
    <section data-testid="amb-dash-posts" className="rounded-3xl border border-[#E3D5C3] bg-[#FBF8F3] p-6 md:p-8">
      <h2 className="font-display text-2xl text-[#141013] md:text-3xl">Mes publications {data?.month ? `de ${monthLabel(data.month)}` : ""}</h2>
      <p className="mt-2 text-sm text-[#5E544D]">Après chaque post ou story créé avec les produits Ozentya, colle ici son lien : Ozentya vérifie tes publications pour valider ta mission du mois. Les stories disparaissent après 24 h : ajoute leur lien le jour même ou garde-les en « À la une ».</p>
      <div className="mt-4 flex flex-wrap gap-2 text-sm">
        <span data-testid="amb-posts-mission" className="rounded-full bg-[#141013] px-4 py-1.5 text-[#F1DBA6]">{MISSION[data?.mission_status] || MISSION["en cours"]}</span>
        <span data-testid="amb-posts-count" className="rounded-full bg-[#E7D3C0] px-4 py-1.5 text-[#141013]">{count("post")} post{count("post") > 1 ? "s" : ""} · {count("story")} stor{count("story") > 1 ? "ies" : "y"}</span>
      </div>
      <PostForm onAdded={refresh} />
      {posts.length ? (
        <ul className="mt-6 divide-y divide-[#E3D5C3]">
          {posts.map((p) => (
            <li key={p.id} data-testid={`amb-post-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm text-[#141013]">
              <span className="flex min-w-0 items-center gap-3"><b className="uppercase">{p.kind}</b>
                <a href={p.url} target="_blank" rel="noopener noreferrer nofollow" className="flex min-w-0 items-center gap-1 truncate text-[#7A5418] underline"><span className="truncate">{p.url}</span><ExternalLink className="h-3 w-3 shrink-0" /></a></span>
              <span className="flex items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-xs ${BADGE[p.status]}`}>{p.status}</span>
                {p.status === "à vérifier" ? <button onClick={() => remove(p.id)} data-testid={`amb-post-delete-${p.id}`} title="Supprimer" className="rounded-full p-1.5 hover:bg-[#F5DADA]"><Trash2 className="h-4 w-4" /></button> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : <p data-testid="amb-posts-empty" className="mt-6 text-sm text-[#5E544D]">Aucun lien ajouté ce mois-ci.</p>}
    </section>
  );
}
